import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';

function CO2Meter({
  value,
  min,
  max,
  size = 140,
  optimalMin,
  optimalMax,
  color,
}) {
  const normalizedValue = Math.min(Math.max((value - min) / (max - min), 0), 1);

  const width = size;
  const height = size * 0.75;

  // Scale factor for fonts (based on default size of 140)
  const scale = size / 140;

  // Determine color based on value, or use custom color
  const getColor = () => {
    if (color) return color;
    if (value < optimalMin) return '#eab308'; // too low - yellow
    if (value > optimalMax) return '#ef4444'; // too high - red
    return '#22c55e'; // optimal - green
  };

  const meterColor = getColor();

  // Create bars for the spectrum analyzer style
  // Scale container padding based on size
  const containerPadding = Math.max(8, 20 * scale);
  const containerLeft = containerPadding;
  const containerRight = width - containerPadding;
  const containerWidth = containerRight - containerLeft;
  const innerPadding = Math.max(4, 10 * scale);
  const barsAreaLeft = containerLeft + innerPadding;
  const barsAreaRight = containerRight - innerPadding;
  const barsAreaWidth = barsAreaRight - barsAreaLeft;

  // Fewer bars for smaller sizes
  const barCount = size < 80 ? 5 : size < 100 ? 8 : 12;
  const barGap = Math.max(2, 4 * scale);
  const totalGapWidth = (barCount - 1) * barGap;
  const barWidth = Math.max(3, (barsAreaWidth - totalGapWidth) / barCount);
  const maxBarHeight = Math.max(10, height - 50);

  // Generate bar heights based on value with some variation
  const getBarHeight = (index) => {
    const centerIndex = barCount / 2;
    const distanceFromCenter = Math.abs(index - centerIndex) / centerIndex;
    const baseHeight = normalizedValue * maxBarHeight;
    const variation = Math.sin((index / barCount) * Math.PI) * 0.3 + 0.7;
    return Math.max(1, baseHeight * variation * (1 - distanceFromCenter * 0.3));
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: size,
      height: height + 20,
    }}>
      <svg width={width} height={height + 20} viewBox={`0 0 ${width} ${height + 20}`}>
        <defs>
          <linearGradient id="co2BarGradient" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={meterColor} stopOpacity={0.6} />
            <stop offset="100%" stopColor={meterColor} stopOpacity={1} />
          </linearGradient>
          <filter id="co2Glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Background container */}
        <rect
          x={containerLeft}
          y={10}
          width={Math.max(20, containerWidth)}
          height={Math.max(20, height - 10)}
          rx={Math.min(12 * scale, Math.max(20, containerWidth) / 4)}
          fill="#111827"
          stroke="#374151"
          strokeWidth={Math.max(1, 2 * scale)}
        />

        {/* CO2 label */}
        <text
          x={width / 2}
          y={30}
          textAnchor="middle"
          fill="#6b7280"
          fontSize={Math.max(8, 11 * scale)}
          fontWeight="500"
        >
          CO₂
        </text>

        {/* Bars */}
        {Array.from({ length: barCount }).map((_, index) => {
          const barHeight = getBarHeight(index);
          const barX = barsAreaLeft + index * (barWidth + barGap);
          const barY = height - 15 - barHeight;

          return (
            <g key={index}>
              {/* Bar background */}
              <rect
                x={barX}
                y={45}
                width={Math.max(1, barWidth)}
                height={Math.max(1, maxBarHeight)}
                rx={Math.max(1, barWidth) / 2}
                fill="#1f2937"
                opacity={0.5}
              />
              {/* Active bar */}
              <rect
                x={barX}
                y={barY + 5}
                width={Math.max(1, barWidth)}
                height={Math.max(1, barHeight)}
                rx={Math.max(1, barWidth) / 2}
                fill="url(#co2BarGradient)"
                filter="url(#co2Glow)"
              >
                <animate
                  attributeName="height"
                  values={`${barHeight};${barHeight * 0.95};${barHeight}`}
                  dur={`${1.5 + index * 0.1}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="y"
                  values={`${barY + 5};${barY + 5 + barHeight * 0.025};${barY + 5}`}
                  dur={`${1.5 + index * 0.1}s`}
                  repeatCount="indefinite"
                />
              </rect>
            </g>
          );
        })}

        {/* Optimal range indicator line */}
        <line
          x1={containerLeft + 5}
          y1={height - 15 - maxBarHeight * ((optimalMin - min) / (max - min))}
          x2={containerRight - 5}
          y2={height - 15 - maxBarHeight * ((optimalMin - min) / (max - min))}
          stroke="#22c55e"
          strokeWidth={1}
          strokeDasharray="4 2"
          opacity={0.5}
        />
        <line
          x1={containerLeft + 5}
          y1={height - 15 - maxBarHeight * ((optimalMax - min) / (max - min))}
          x2={containerRight - 5}
          y2={height - 15 - maxBarHeight * ((optimalMax - min) / (max - min))}
          stroke="#22c55e"
          strokeWidth={1}
          strokeDasharray="4 2"
          opacity={0.5}
        />

        {/* Floating particles effect */}
        {[...Array(Math.max(3, Math.floor(6 * scale)))].map((_, i) => {
          // Use deterministic positions based on index instead of random
          const xOffset = ((i * 37) % 100) / 100;
          const yOffset = ((i * 53) % 100) / 100;
          return (
            <circle
              key={i}
              cx={barsAreaLeft + xOffset * barsAreaWidth}
              cy={50 + yOffset * Math.max(10, height - 80)}
              r={Math.max(1, 2 * scale)}
              fill={meterColor}
              opacity={0.4}
            >
              <animate
                attributeName="cy"
                values={`${50 + i * 10 * scale};${40 + i * 10 * scale};${50 + i * 10 * scale}`}
                dur={`${2 + i * 0.3}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0.4;0.7;0.4"
                dur={`${2 + i * 0.3}s`}
                repeatCount="indefinite"
              />
            </circle>
          );
        })}

        {/* Value display */}
        <text
          x={width / 2}
          y={height + 12}
          textAnchor="middle"
          fill="#9ca3af"
          fontSize={Math.max(8, 10 * scale)}
        >
          {value} ppm
        </text>
      </svg>
    </Box>
  );
}

CO2Meter.propTypes = {
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  size: PropTypes.number,
  optimalMin: PropTypes.number,
  optimalMax: PropTypes.number,
  color: PropTypes.string,
};

export default CO2Meter;
